import CircularProgress from '@mui/material/CircularProgress';
import { useErrorContext } from '@qpr/client-library';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMore from 'highcharts/highcharts-more';
import React, { useEffect, useState } from 'react';
import { useDashboard } from '../../Dashboard/DashboardContext';
import { Data } from './highchart-component-state';
import createHighchartsViewModel, { HighchartsViewModelType } from './highcharts-view-model';

HighchartsMore(Highcharts);

interface HighchartProps {
  title?: string;
  meaId?: string;
  query?: string;
  selectedPeriod?: string;
  noUrlOverride?: boolean;
  series?: string[];
  periodsBefore?: string;
  periodsAfter?: string;
  chartOptions?: any;
}

const HighchartsComponent = ({
  title,
  meaId,
  query,
  selectedPeriod,
  noUrlOverride,
  series,
  periodsBefore,
  periodsAfter,
  chartOptions
}: HighchartProps) => {
  const [data, setData] = useState<Data[]>();
  const [queryString] = useDashboard();
  const { setError } = useErrorContext();

  const viewModel: HighchartsViewModelType = createHighchartsViewModel({
    id: (!noUrlOverride && queryString.get('meaId')) || meaId || '',
    query: query || '',
    period: (!noUrlOverride && queryString.get('period')) || selectedPeriod || '',
    series: (!noUrlOverride && queryString.get('series')?.split(',')) || series || [],
    periodsBefore: (!noUrlOverride && queryString.get('periodsBefore')) || periodsBefore || '',
    periodsAfter: (!noUrlOverride && queryString.get('periodsAfter')) || periodsAfter || ''
  });

  const groupBy = (arr: any[], property: string) => {
    return arr.reduce((memo, x) => {
      if (!memo[x[property]]) {
        memo[x[property]] = [];
      }

      memo[x[property]].push(x);
      return memo;
    }, {});
  };

  const getSeries = (datax: any[]) => {
    const o = groupBy(datax, 'series');

    const final: any[] = [];

    Object.entries(o).forEach(([name, value]) => {
      const chartOption =
        chartOptions?.series?.filter((serie: any) => serie.meaSymbol === name).pop() || {};
      final.push({
        name,
        data: (value as any[]).map((item: any) => {
          return {
            y: item.value || item.value === 0 ? Number(item.value) : null,
            name: item.period
          };
        }),
        ...chartOption
      });
    });

    return final || [];
  };

  useEffect(() => {
    const initData = async () => {
      const gotData = await viewModel.getData();
      setData(getSeries(gotData.data));
      if (gotData.error?.message) setError(gotData.error);
    };
    initData();
  }, [queryString]);

  const defaultChartOptions = {
    chart: { type: 'column' },
    title: {
      text: title
    },
    xAxis: {
      type: 'category'
    },
    credits: { enabled: false }
  };

  const updatedChartOpts = {
    ...defaultChartOptions,
    ...chartOptions,
    series: data
  };

  return data ? (
    <div data-testid="highchart">
      <HighchartsReact highcharts={Highcharts} options={updatedChartOpts} />
    </div>
  ) : (
    <div
      data-testid="loadingIndicator"
      style={{ height: chartOptions?.chart?.height || '400px', margin: 'auto' }}
    >
      <CircularProgress />
    </div>
  );
};

export default HighchartsComponent;
